export const M9_UNIT_10 = {
  id: "m9-u10-conditional-imperative",
  title: "Unidad 10: Solving Problems & Giving Advice",
  topic: "Give powerful advice! Learn to combine first conditional with imperatives to solve problems and help others. Perfect for health, lifestyle, and everyday situations!",
  iconType: "heart",
  grammarImage: "/grammar/grammar-m9-u10.jpg",
  grammarText: "FIRST CONDITIONAL + IMPERATIVE: IF + present simple, IMPERATIVE! 'If you're tired, rest!' Use it for direct advice and clear instructions. No WILL needed!",
  
  sets: [
    // SET 1
    {
      id: "m9-u10-s1",
      title: "Set 1: Basic Advice Pattern - Recognition 💡",
      context: `**Doctor's Office**

Doctor: "If you're sick, rest at home. If you have a fever, drink lots of water. These are simple but important instructions!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which gives direct advice using IF?",
          options: [
            "You should rest",
            "If you're tired, rest",
            "Rest is important"
          ],
          a: "If you're tired, rest",
          tip: "IF + problem, SOLUTION (imperative)!",
          successtip: "✓ DIRECT ADVICE!\nIF + imperative gives clear, direct instructions! Perfect for health advice! 💊"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tienes sed, bebe agua'",
          words: ["If", "you're", "thirsty", "drink", "water"],
          a: "If you're thirsty drink water",
          extra: ["will", "drinks"],
          tip: "No uses WILL después de la coma!",
          successtip: "✓ IMPERATIVE!\nDespués de la coma, usa el IMPERATIVO (drink), no 'will drink'! 💧"
        },
        {
          type: "mc",
          q: "What's different from regular first conditional?",
          options: [
            "If you're cold, you'll freeze",
            "If you're cold, put on a coat",
            "If you were cold, wear a coat"
          ],
          a: "If you're cold, put on a coat",
          tip: "IMPERATIVE (put on) instead of WILL!",
          successtip: "✓ NO WILL!\nImperative = direct command. Much stronger than 'you'll feel better'! 🧥"
        },
        {
          type: "input",
          q: "Complete: 'If you feel sick, _____ a doctor.' (call)",
          a: "call",
          tip: "Use base form of verb (imperative)!",
          successtip: "✓ CALL!\nImperative = base form of the verb. Simple and direct! 📞"
        },
        {
          type: "match",
          q: "Match the problem with the advice:",
          matches: [
            { left: "If you're hungry,", right: "eat something" },
            { left: "If you're tired,", right: "go to bed" },
            { left: "If you're cold,", right: "wear a jacket" }
          ],
          successtip: "✓ BASIC ADVICE!\nThese are the most common everyday advice patterns! 🎯"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tienes hambre, come algo'",
          words: ["If", "you're", "hungry", "eat", "something"],
          a: "If you're hungry eat something",
          extra: ["will", "eating"],
          successtip: "✓ SIMPLE ADVICE!\nThis pattern works for ANY basic need: hungry→eat, tired→sleep! 🍽️"
        },
        {
          type: "mc",
          q: "Which is the imperative form?",
          options: [
            "If you're stressed, you relax",
            "If you're stressed, relax",
            "If you're stressed, you'll relax"
          ],
          a: "If you're stressed, relax",
          tip: "Just the verb, no 'you' or 'will'!",
          successtip: "✓ IMPERATIVE!\nImperative = verb only! No subject (you), no auxiliary (will)! 😌"
        },
        {
          type: "input",
          q: "Complete: 'If you have a headache, _____ some medicine.' (take)",
          a: "take",
          successtip: "✓ TAKE!\nTAKE medicine, DRINK water, EAT food - essential health verbs! 💊"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si estás cansado, descansa'",
          words: ["If", "you're", "tired", "rest"],
          a: "If you're tired rest",
          extra: ["will", "resting"],
          successtip: "✓ REST!\nThe simplest and most important health advice! 😴"
        },
        {
          type: "mc",
          q: "What makes this advice direct and strong?",
          options: [
            "If you're hot, you should open the window",
            "If you're hot, open the window",
            "When you're hot, you open the window"
          ],
          a: "If you're hot, open the window",
          tip: "Imperative = strongest form!",
          successtip: "✓ DIRECT!\nImperative makes advice sound more urgent and important! 🪟"
        }
      ]
    },

    // SET 2
    {
      id: "m9-u10-s2",
      title: "Set 2: Health & Wellness Vocabulary 🏥",
      context: `**Health Tips Article**

"Simple health advice: If you feel unwell, rest. If you're stressed, take a break. If you can't sleep, turn off your phone..."`,
      
      questions: [
        {
          type: "mc",
          q: "What does 'feel unwell' mean?",
          options: [
            "Feel sick or not good",
            "Feel very well",
            "Feel excited"
          ],
          a: "Feel sick or not good",
          successtip: "✓ UNWELL!\nUNWELL = not feeling healthy. Common in British English! 🤒"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si estás estresado, toma un descanso'",
          words: ["If", "you're", "stressed", "take", "a", "break"],
          a: "If you're stressed take a break",
          extra: ["will", "taking"],
          successtip: "✓ TAKE A BREAK!\nSuper common phrase! Break = descanso corto! ☕"
        },
        {
          type: "input",
          q: "Complete: 'If you can't sleep, _____ off your phone.' (turn)",
          a: "turn",
          tip: "Turn off = apagar!",
          successtip: "✓ TURN OFF!\nTURN OFF (apagar) vs TURN ON (encender). Essential phrasal verb! 📱"
        },
        {
          type: "match",
          q: "Match the health problem with advice:",
          matches: [
            { left: "If you have a fever,", right: "drink lots of water" },
            { left: "If you have a cold,", right: "get plenty of rest" },
            { left: "If you feel dizzy,", right: "sit down" }
          ],
          successtip: "✓ HEALTH ADVICE!\nThese are universal health tips doctors give! 🩺"
        },
        {
          type: "mc",
          q: "What does 'get plenty of rest' mean?",
          options: [
            "Sleep a little bit",
            "Get lots of rest/sleep",
            "Don't sleep"
          ],
          a: "Get lots of rest/sleep",
          tip: "'Plenty of' = lots of, much!",
          successtip: "✓ PLENTY OF!\nPLENTY OF = mucho/a, suficiente. Very common in advice! 💤"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si te sientes mareado, siéntate'",
          words: ["If", "you", "feel", "dizzy", "sit", "down"],
          a: "If you feel dizzy sit down",
          extra: ["will", "sitting"],
          tip: "Dizzy = mareado!",
          successtip: "✓ DIZZY!\nFeel dizzy = sentirse mareado. SIT DOWN = safety advice! 💺"
        },
        {
          type: "input",
          q: "Complete: 'If you're dehydrated, _____ lots of water.' (drink)",
          a: "drink",
          tip: "Dehydrated = deshidratado!",
          successtip: "✓ DEHYDRATED!\nDehydrated = need water urgently! Medical vocabulary! 💧"
        },
        {
          type: "match",
          q: "Match the wellness advice:",
          matches: [
            { left: "If you're anxious,", right: "breathe deeply" },
            { left: "If you're tense,", right: "stretch your muscles" },
            { left: "If you're overwhelmed,", right: "take it slow" }
          ],
          successtip: "✓ MENTAL HEALTH!\nMental wellness advice is just as important as physical! 🧘"
        },
        {
          type: "mc",
          q: "Complete: 'If you work too much, _____.'",
          options: [
            "work more",
            "take some time off",
            "be tired"
          ],
          a: "take some time off",
          tip: "'Time off' = vacation/break from work!",
          successtip: "✓ TIME OFF!\nTAKE TIME OFF = tomarse un descanso del trabajo. Work-life balance! 🏖️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tienes dolor de cabeza, toma un analgésico'",
          words: ["If", "you", "have", "a", "headache", "take", "a", "painkiller"],
          a: "If you have a headache take a painkiller",
          extra: ["will", "painkillers"],
          successtip: "✓ PAINKILLER!\nPainkiller = analgésico (medicine for pain). Essential vocabulary! 💊"
        }
      ]
    },

    // SET 3
    {
      id: "m9-u10-s3",
      title: "Set 3: Negative Imperatives - Don't Commands! 🚫",
      context: `**Health Warnings**

"Important: If you're sick, don't go to work. If you're tired, don't drive. If you have allergies, don't eat that food!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which gives a negative command?",
          options: [
            "If you're sick, stay home",
            "If you're sick, don't go to work",
            "If you're sick, you won't work"
          ],
          a: "If you're sick, don't go to work",
          tip: "DON'T + verb = negative imperative!",
          successtip: "✓ DON'T!\nDON'T + verb for negative commands! Much stronger than 'you shouldn't'! ⚠️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si estás cansado, no conduzcas'",
          words: ["If", "you're", "tired", "don't", "drive"],
          a: "If you're tired don't drive",
          extra: ["doesn't", "won't"],
          tip: "DON'T (not doesn't) para imperativos!",
          successtip: "✓ SAFETY WARNING!\nDON'T drive when tired = critical safety advice! 🚗"
        },
        {
          type: "input",
          q: "Complete: 'If you have a fever, _____ exercise.' (don't)",
          a: "don't",
          tip: "Negative imperative = DON'T + verb!",
          successtip: "✓ DON'T EXERCISE!\nExercise with fever = dangerous! Strong medical advice! 🤒"
        },
        {
          type: "match",
          q: "Match the warning:",
          matches: [
            { left: "If it's raining hard,", right: "don't go out" },
            { left: "If you're allergic,", right: "don't eat it" },
            { left: "If you're on medication,", right: "don't drink alcohol" }
          ],
          successtip: "✓ WARNINGS!\nNegative imperatives are essential for safety warnings! ⚠️"
        },
        {
          type: "mc",
          q: "Which is the correct negative imperative?",
          options: [
            "If you're full, doesn't eat more",
            "If you're full, don't eat more",
            "If you're full, not eat more"
          ],
          a: "If you're full, don't eat more",
          tip: "Always DON'T (not doesn't or not)!",
          successtip: "✓ DON'T EAT!\nDON'T = imperative. DOESN'T = third person present. Don't confuse! 🍽️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tienes prisa, no corras'",
          words: ["If", "you're", "in", "a", "hurry", "don't", "run"],
          a: "If you're in a hurry don't run",
          extra: ["doesn't", "running"],
          tip: "'In a hurry' = con prisa!",
          successtip: "✓ DON'T RUN!\nSafety advice: rushing causes accidents! 🏃"
        },
        {
          type: "input",
          q: "Complete: 'If you feel dizzy, _____ stand up quickly.' (don't)",
          a: "don't",
          successtip: "✓ MEDICAL ADVICE!\nStanding up quickly when dizzy = dangerous! Important warning! 💫"
        },
        {
          type: "match",
          q: "Match the health warning:",
          matches: [
            { left: "If you're pregnant,", right: "don't smoke" },
            { left: "If you're diabetic,", right: "don't eat too much sugar" },
            { left: "If you're injured,", right: "don't move too much" }
          ],
          successtip: "✓ MEDICAL WARNINGS!\nThese imperatives can save lives! Critical health advice! 🏥"
        },
        {
          type: "mc",
          q: "Complete the safety warning: 'If the light is red, _____.'",
          options: [
            "go quickly",
            "don't cross",
            "you cross"
          ],
          a: "don't cross",
          successtip: "✓ TRAFFIC SAFETY!\nTraffic warnings use imperatives worldwide! 🚦"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si está oscuro, no camines solo'",
          words: ["If", "it's", "dark", "don't", "walk", "alone"],
          a: "If it's dark don't walk alone",
          extra: ["doesn't", "won't"],
          successtip: "✓ SAFETY TIP!\nPersonal safety advice using negative imperative! 🌙"
        }
      ]
    },

    // SET 4
    {
      id: "m9-u10-s4",
      title: "Set 4: Lifestyle & Habits Advice 🌟",
      context: `**Life Coach Tips**

"Want a better life? If you're always stressed, slow down. If you spend too much, save more. If you're unhappy, make changes!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which gives lifestyle advice?",
          options: [
            "If you're always busy, you're tired",
            "If you're always busy, take a break",
            "If you were busy, you'd rest"
          ],
          a: "If you're always busy, take a break",
          successtip: "✓ LIFESTYLE ADVICE!\nImperative makes self-help advice direct and actionable! 📈"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si gastas demasiado, ahorra más'",
          words: ["If", "you", "spend", "too", "much", "save", "more"],
          a: "If you spend too much save more",
          extra: ["will", "spending"],
          successtip: "✓ FINANCIAL ADVICE!\nSpend vs Save = basic financial wisdom! 💰"
        },
        {
          type: "input",
          q: "Complete: 'If you're unhappy, _____ changes.' (make)",
          a: "make",
          tip: "Make changes = hacer cambios!",
          successtip: "✓ MAKE CHANGES!\nEmpowering advice! You control your life! 🔄"
        },
        {
          type: "match",
          q: "Match the lifestyle problem with advice:",
          matches: [
            { left: "If you work too hard,", right: "take time off" },
            { left: "If you eat badly,", right: "improve your diet" },
            { left: "If you never exercise,", right: "start small" }
          ],
          successtip: "✓ SELF-IMPROVEMENT!\nThese are the top 3 life coach recommendations! 💪"
        },
        {
          type: "mc",
          q: "What does 'slow down' mean?",
          options: [
            "Go faster",
            "Do things more slowly and calmly",
            "Stop everything"
          ],
          a: "Do things more slowly and calmly",
          tip: "Slow down = reducir el ritmo!",
          successtip: "✓ SLOW DOWN!\nModern life advice: SLOW DOWN to reduce stress! 🐌"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tienes demasiadas posesiones, deshazte de algunas'",
          words: ["If", "you", "have", "too", "many", "possessions", "get", "rid", "of", "some"],
          a: "If you have too many possessions get rid of some",
          extra: ["will", "possession"],
          tip: "Get rid of = deshacerse de!",
          successtip: "✓ MINIMALISM!\nGET RID OF = essential phrase for decluttering advice! 📦"
        },
        {
          type: "input",
          q: "Complete: 'If you're always checking emails, _____ your notifications.' (turn off)",
          a: "turn off",
          alts: ["disable"],
          successtip: "✓ DIGITAL DETOX!\nTurn off notifications = modern wellness advice! 📵"
        },
        {
          type: "match",
          q: "Match the modern lifestyle advice:",
          matches: [
            { left: "If you're on social media too much,", right: "limit your screen time" },
            { left: "If you're always multitasking,", right: "focus on one thing" },
            { left: "If you're sleep deprived,", right: "prioritize rest" }
          ],
          successtip: "✓ MODERN PROBLEMS!\n21st century lifestyle advice! Very relevant! 📱"
        },
        {
          type: "mc",
          q: "Complete: 'If you live in a big house you don't need, _____.'",
          options: [
            "buy a bigger one",
            "consider downsizing",
            "stay there forever"
          ],
          a: "consider downsizing",
          tip: "Downsize = move to smaller home!",
          successtip: "✓ DOWNSIZE!\nDownsizing = simplifying life. Popular trend! 🏠"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si quieres sentirte más tranquilo, planifica tu día'",
          words: ["If", "you", "want", "to", "feel", "calmer", "plan", "your", "day"],
          a: "If you want to feel calmer plan your day",
          extra: ["will", "planning"],
          successtip: "✓ ORGANIZATION!\nPlanning reduces stress! Productivity advice! 📅"
        }
      ]
    },

    // SET 5
    {
      id: "m9-u10-s5",
      title: "Set 5: Reversible Order - Imperative First! 🔄",
      context: `**Quick Tips**

"You can reverse the order! 'Take a break if you're tired' OR 'If you're tired, take a break.' Same meaning, different emphasis!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which two sentences mean the SAME thing?",
          options: [
            "Rest if you're tired / If you're tired, rest",
            "Rest if you're tired / Rest when you're tired",
            "Rest if you're tired / You should rest"
          ],
          a: "Rest if you're tired / If you're tired, rest",
          tip: "Order can be reversed!",
          successtip: "✓ REVERSIBLE!\nYou can put the imperative FIRST! No comma needed! 🔄"
        },
        {
          type: "traduce",
          q: "Traduce (empezando con el imperativo): 'Llama si necesitas ayuda'",
          words: ["Call", "if", "you", "need", "help"],
          a: "Call if you need help",
          extra: ["will", "calling"],
          tip: "No comma when imperative comes first!",
          successtip: "✓ NO COMMA!\nWhen imperative is first, NO comma before IF! 📞"
        },
        {
          type: "input",
          q: "Reverse this: 'If you're hungry, eat.' → '_____ if you're hungry.'",
          a: "Eat",
          alts: ["eat"],
          successtip: "✓ REVERSED!\nBoth orders are correct! Choose based on emphasis! 🍽️"
        },
        {
          type: "match",
          q: "Match the standard with the reversed version:",
          matches: [
            { left: "If you're cold, wear a coat", right: "Wear a coat if you're cold" },
            { left: "If you're lost, ask for help", right: "Ask for help if you're lost" },
            { left: "If it rains, take an umbrella", right: "Take an umbrella if it rains" }
          ],
          successtip: "✓ BOTH CORRECT!\nMeaning is identical! Reversed version sounds more casual! 🌂"
        },
        {
          type: "mc",
          q: "Which needs a comma?",
          options: [
            "Call me if you need help",
            "If you need help, call me",
            "Both need commas"
          ],
          a: "If you need help, call me",
          tip: "Comma only when IF starts the sentence!",
          successtip: "✓ COMMA RULE!\nIF first = comma. Imperative first = NO comma! ✅"
        },
        {
          type: "traduce",
          q: "Traduce: 'Bebe agua si tienes sed'",
          words: ["Drink", "water", "if", "you're", "thirsty"],
          a: "Drink water if you're thirsty",
          extra: ["will", "drinking"],
          successtip: "✓ IMPERATIVE FIRST!\nThis order feels more direct and commanding! 💧"
        },
        {
          type: "input",
          q: "Add punctuation: 'If you feel sick call a doctor'",
          a: "If you feel sick, call a doctor",
          alts: ["If you feel sick, call a doctor."],
          tip: "Comma after the IF clause!",
          successtip: "✓ PUNCTUATION!\nAlways comma after IF clause when it comes first! ⚕️"
        },
        {
          type: "mc",
          q: "Which sounds MORE urgent?",
          options: [
            "If there's a fire, leave immediately",
            "Leave immediately if there's a fire",
            "They sound the same"
          ],
          a: "Leave immediately if there's a fire",
          tip: "Imperative first = more urgent!",
          successtip: "✓ URGENCY!\nImperative first emphasizes the ACTION more! 🚨"
        },
        {
          type: "traduce",
          q: "Traduce (orden estándar): 'Si ves algo sospechoso, informa a la policía'",
          words: ["If", "you", "see", "something", "suspicious", "report", "it", "to", "the", "police"],
          a: "If you see something suspicious report it to the police",
          extra: ["will", "saw"],
          successtip: "✓ PUBLIC SAFETY!\n'See something, say something' = famous safety slogan! 👮"
        },
        {
          type: "match",
          q: "Choose the better order for each context:",
          matches: [
            { left: "Casual conversation:", right: "Call me if you need anything" },
            { left: "Formal instruction:", right: "If symptoms persist, consult a doctor" },
            { left: "Emergency:", right: "Run if you see danger" }
          ],
          successtip: "✓ CONTEXT MATTERS!\nImperative first = casual. IF first = formal. Emergency = imperative first! 🎯"
        }
      ]
    },

    // SET 6
    {
      id: "m9-u10-s6",
      title: "Set 6: Problem-Solving at Work 💼",
      context: `**Office Manager's Email**

"Team: If you have technical issues, contact IT. If you're overwhelmed with tasks, delegate. If you need resources, request them!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which is professional workplace advice?",
          options: [
            "If you're stressed, cry",
            "If you're overwhelmed, delegate tasks",
            "If you're busy, complain"
          ],
          a: "If you're overwhelmed, delegate tasks",
          tip: "Delegate = assign work to others!",
          successtip: "✓ DELEGATE!\nDelegating is a KEY professional skill! B1-B2 vocabulary! 👔"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tienes problemas técnicos, contacta a IT'",
          words: ["If", "you", "have", "technical", "issues", "contact", "IT"],
          a: "If you have technical issues contact IT",
          extra: ["will", "contacted"],
          tip: "IT = Information Technology!",
          successtip: "✓ WORKPLACE!\nIT support = essential in modern offices! 💻"
        },
        {
          type: "input",
          q: "Complete: 'If you need resources, _____ them.' (request)",
          a: "request",
          tip: "Request = formal way to say 'ask for'!",
          successtip: "✓ REQUEST!\nREQUEST is more formal than ASK FOR. Professional vocabulary! 📋"
        },
        {
          type: "match",
          q: "Match the work problem with the solution:",
          matches: [
            { left: "If you miss a deadline,", right: "inform your manager immediately" },
            { left: "If you're unclear about tasks,", right: "ask for clarification" },
            { left: "If you make a mistake,", right: "acknowledge it and fix it" }
          ],
          successtip: "✓ PROFESSIONAL CONDUCT!\nThese are signs of mature, responsible employees! 🌟"
        },
        {
          type: "mc",
          q: "What does 'overwhelmed' mean?",
          options: [
            "Very excited",
            "Having too much to handle",
            "Very organized"
          ],
          a: "Having too much to handle",
          tip: "Overwhelmed = abrumado!",
          successtip: "✓ OVERWHELMED!\nCommon feeling at work! Important B2 vocabulary! 😰"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si necesitas aclaración, pregunta en la reunión'",
          words: ["If", "you", "need", "clarification", "ask", "in", "the", "meeting"],
          a: "If you need clarification ask in the meeting",
          extra: ["will", "asking"],
          successtip: "✓ CLARIFICATION!\nAsking for clarification = sign of professionalism! 🗣️"
        },
        {
          type: "input",
          q: "Complete: 'If you encounter obstacles, _____ creative solutions.' (find)",
          a: "find",
          tip: "Obstacles = obstáculos!",
          successtip: "✓ PROBLEM-SOLVING!\nFinding creative solutions = valued skill in business! 💡"
        },
        {
          type: "match",
          q: "Match the professional imperative:",
          matches: [
            { left: "If you're running late,", right: "notify everyone in advance" },
            { left: "If you disagree,", right: "express it respectfully" },
            { left: "If you need help,", right: "don't hesitate to ask" }
          ],
          successtip: "✓ PROFESSIONAL ETIQUETTE!\nThese show emotional intelligence at work! 🤝"
        },
        {
          type: "mc",
          q: "Complete professionally: 'If the project is behind schedule, _____.'",
          options: [
            "panic and quit",
            "reassess priorities and adjust timelines",
            "ignore it"
          ],
          a: "reassess priorities and adjust timelines",
          tip: "Reassess = re-evaluate!",
          successtip: "✓ PROJECT MANAGEMENT!\nReassessing and adjusting = professional crisis management! 📊"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si el cliente se queja, escucha activamente y busca soluciones'",
          words: ["If", "the", "client", "complains", "listen", "actively", "and", "seek", "solutions"],
          a: "If the client complains listen actively and seek solutions",
          extra: ["will", "complained"],
          tip: "Seek = buscar (formal)!",
          successtip: "✓ CUSTOMER SERVICE!\nActive listening + solution-seeking = excellent service! 🌟"
        }
      ]
    },

    // SET 7
    {
      id: "m9-u10-s7",
      title: "Set 7: Emergency & Safety Instructions 🚨",
      context: `**Emergency Procedures**

"IMPORTANT: If you smell gas, evacuate immediately. If there's a fire, use the stairs, not the elevator. If someone is injured, call 911!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which is the MOST urgent emergency instruction?",
          options: [
            "If there's a fire, you should leave",
            "If there's a fire, leave immediately",
            "If there's a fire, it's dangerous"
          ],
          a: "If there's a fire, leave immediately",
          tip: "Imperative + 'immediately' = maximum urgency!",
          successtip: "✓ EMERGENCY!\nImperative is PERFECT for life-or-death instructions! 🔥"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hueles gas, evacua inmediatamente'",
          words: ["If", "you", "smell", "gas", "evacuate", "immediately"],
          a: "If you smell gas evacuate immediately",
          extra: ["will", "smelled"],
          successtip: "✓ LIFE-SAVING!\nEvacuate immediately = saves lives! Critical vocabulary! ⚠️"
        },
        {
          type: "input",
          q: "Complete: 'If someone stops breathing, _____ CPR.' (perform)",
          a: "perform",
          tip: "Perform CPR = realizar RCP!",
          successtip: "✓ FIRST AID!\nPerform CPR = essential emergency skill! Medical imperative! 🆘"
        },
        {
          type: "match",
          q: "Match the emergency with the instruction:",
          matches: [
            { left: "If there's an earthquake,", right: "take cover under a desk" },
            { left: "If you're choking,", right: "signal for help immediately" },
            { left: "If there's a tornado,", right: "go to the basement" }
          ],
          successtip: "✓ EMERGENCY PROTOCOLS!\nThese imperatives can save your life! 🌪️"
        },
        {
          type: "mc",
          q: "What does 'take cover' mean?",
          options: [
            "Take a blanket",
            "Hide and protect yourself",
            "Run outside"
          ],
          a: "Hide and protect yourself",
          tip: "Take cover = protegerse!",
          successtip: "✓ TAKE COVER!\nEssential safety vocabulary for disasters! 🛡️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si alguien está lesionado, llama al 911'",
          words: ["If", "someone", "is", "injured", "call", "911"],
          a: "If someone is injured call 911",
          extra: ["will", "calls"],
          successtip: "✓ EMERGENCY SERVICES!\n911 (US), 999 (UK), 112 (EU) = emergency numbers! 🚑"
        },
        {
          type: "input",
          q: "Complete: 'If the building shakes, _____ calm.' (stay)",
          a: "stay",
          tip: "Stay calm = mantén la calma!",
          successtip: "✓ STAY CALM!\nMost important emergency advice! Panic kills! 😌"
        },
        {
          type: "match",
          q: "Match the safety warning:",
          matches: [
            { left: "If you see sparks,", right: "don't touch electrical equipment" },
            { left: "If water is rising,", right: "move to higher ground" },
            { left: "If you hear an alarm,", right: "follow evacuation procedures" }
          ],
          successtip: "✓ SAFETY FIRST!\nNegative imperatives prevent dangerous actions! ⚡"
        },
        {
          type: "mc",
          q: "Which shows the correct emergency procedure?",
          options: [
            "If there's smoke, use the elevator",
            "If there's smoke, use the stairs",
            "If there's smoke, stay where you are"
          ],
          a: "If there's smoke, use the stairs",
          tip: "NEVER use elevators in fires!",
          successtip: "✓ FIRE SAFETY!\nElevators can trap you! Always use stairs! 🏃‍♂️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hay una amenaza, sigue los protocolos de seguridad'",
          words: ["If", "there's", "a", "threat", "follow", "safety", "protocols"],
          a: "If there's a threat follow safety protocols",
          extra: ["will", "followed"],
          successtip: "✓ PROTOCOLS!\nFollowing protocols = disciplined emergency response! 📋"
        }
      ]
    },

    // SET 8
    {
      id: "m9-u10-s8",
      title: "Set 8: Technology Troubleshooting 💻",
      context: `**IT Help Desk**

"Common fixes: If your computer freezes, restart it. If you can't connect, check your WiFi. If it's slow, close unnecessary programs."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is practical tech advice?",
          options: [
            "If your computer is slow, buy a new one",
            "If your computer is slow, close unnecessary programs",
            "If your computer is slow, do nothing"
          ],
          a: "If your computer is slow, close unnecessary programs",
          successtip: "✓ TECH SUPPORT!\nFirst conditional + imperative = standard IT troubleshooting! 🖥️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tu computadora se congela, reiníciala'",
          words: ["If", "your", "computer", "freezes", "restart", "it"],
          a: "If your computer freezes restart it",
          extra: ["will", "restarting"],
          tip: "Freeze = congelarse (computers)!",
          successtip: "✓ RESTART!\nRestarting fixes 80% of computer problems! Universal advice! 🔄"
        },
        {
          type: "input",
          q: "Complete: 'If you can't connect, _____ your WiFi settings.' (check)",
          a: "check",
          successtip: "✓ CONNECTIVITY!\nCheck WiFi = first step in network troubleshooting! 📶"
        },
        {
          type: "match",
          q: "Match the tech problem with the solution:",
          matches: [
            { left: "If your phone battery drains fast,", right: "disable background apps" },
            { left: "If you get an error message,", right: "screenshot it and report it" },
            { left: "If your device overheats,", right: "turn it off and let it cool" }
          ],
          successtip: "✓ DEVICE CARE!\nThese tips extend device lifespan! 📱"
        },
        {
          type: "mc",
          q: "What does 'disable' mean?",
          options: [
            "Turn on",
            "Turn off / deactivate",
            "Delete permanently"
          ],
          a: "Turn off / deactivate",
          tip: "Disable ↔ Enable!",
          successtip: "✓ DISABLE!\nDisable (desactivar) vs Enable (activar). Tech vocabulary! ⚙️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si el problema persiste, actualiza el software'",
          words: ["If", "the", "problem", "persists", "update", "the", "software"],
          a: "If the problem persists update the software",
          extra: ["will", "persist"],
          tip: "Persist = continuar/persistir!",
          successtip: "✓ PERSIST!\n'If the problem persists' = standard tech support phrase! 🔧"
        },
        {
          type: "input",
          q: "Complete: 'If you're infected with malware, _____ a full scan.' (run)",
          a: "run",
          tip: "Run a scan = ejecutar un escaneo!",
          successtip: "✓ SECURITY!\nRun a scan = essential cybersecurity action! 🛡️"
        },
        {
          type: "match",
          q: "Match the software issue with the fix:",
          matches: [
            { left: "If the app crashes,", right: "clear the cache and restart" },
            { left: "If you forgot your password,", right: "use the recovery option" },
            { left: "If files are corrupted,", right: "restore from backup" }
          ],
          successtip: "✓ SOFTWARE FIXES!\nEvery tech user needs to know these! 💾"
        },
        {
          type: "mc",
          q: "Complete: 'If you receive a suspicious email, _____.'",
          options: [
            "open it immediately",
            "don't click any links",
            "forward it to everyone"
          ],
          a: "don't click any links",
          tip: "Negative imperative for security!",
          successtip: "✓ CYBERSECURITY!\nDON'T click suspicious links = #1 security rule! 🔒"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tu dispositivo no responde, fuerza el reinicio'",
          words: ["If", "your", "device", "doesn't", "respond", "force", "restart", "it"],
          a: "If your device doesn't respond force restart it",
          extra: ["will", "restarting"],
          successtip: "✓ FORCE RESTART!\nForce restart = last resort when device is frozen! 🔋"
        }
      ]
    },

    // SET 9
    {
      id: "m9-u10-s9",
      title: "Set 9: Financial & Money Management 💰",
      context: `**Financial Advisor**

"Smart money habits: If you can't afford it, don't buy it. If you get a raise, save the difference. If you have debt, prioritize paying it off."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is sound financial advice?",
          options: [
            "If you see something nice, buy it immediately",
            "If you can't afford it, don't buy it",
            "If you want it, use credit"
          ],
          a: "If you can't afford it, don't buy it",
          tip: "Afford = poder pagar!",
          successtip: "✓ FINANCIAL DISCIPLINE!\nThis simple rule prevents debt! Basic money wisdom! 💳"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si recibes un aumento, ahorra la diferencia'",
          words: ["If", "you", "get", "a", "raise", "save", "the", "difference"],
          a: "If you get a raise save the difference",
          extra: ["will", "getting"],
          tip: "Raise = aumento de salario!",
          successtip: "✓ SMART SAVING!\nSave raises instead of spending = wealth-building strategy! 📈"
        },
        {
          type: "input",
          q: "Complete: 'If you have debt, _____ paying it off.' (prioritize)",
          a: "prioritize",
          tip: "Prioritize = dar prioridad!",
          successtip: "✓ DEBT MANAGEMENT!\nPrioritizing debt payment = financial freedom! 🎯"
        },
        {
          type: "match",
          q: "Match the money situation with advice:",
          matches: [
            { left: "If you're living paycheck to paycheck,", right: "create a budget" },
            { left: "If you have no emergency fund,", right: "start saving immediately" },
            { left: "If you're overspending,", right: "track your expenses" }
          ],
          successtip: "✓ FINANCIAL WELLNESS!\nThese are foundations of financial health! 💪"
        },
        {
          type: "mc",
          q: "What does 'living paycheck to paycheck' mean?",
          options: [
            "Being very rich",
            "Spending all your salary before the next one",
            "Having lots of savings"
          ],
          a: "Spending all your salary before the next one",
          tip: "No money left between paychecks!",
          successtip: "✓ COMMON STRUGGLE!\nPaycheck to paycheck = no savings, high stress! Common reality! 😰"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si quieres ahorrar dinero, haz un presupuesto'",
          words: ["If", "you", "want", "to", "save", "money", "make", "a", "budget"],
          a: "If you want to save money make a budget",
          extra: ["will", "making"],
          successtip: "✓ BUDGETING!\nMaking a budget = first step to financial control! 📊"
        },
        {
          type: "input",
          q: "Complete: 'If you find yourself overspending, _____ your credit cards at home.' (leave)",
          a: "leave",
          tip: "Overspending = gastar demasiado!",
          successtip: "✓ IMPULSE CONTROL!\nLeaving cards at home prevents impulse buying! 🛍️"
        },
        {
          type: "match",
          q: "Match the investment advice:",
          matches: [
            { left: "If you're new to investing,", right: "start with index funds" },
            { left: "If you don't understand an investment,", right: "don't invest in it" },
            { left: "If you're nearing retirement,", right: "reduce risk" }
          ],
          successtip: "✓ INVESTMENT WISDOM!\nThese principles protect your money! 📉📈"
        },
        {
          type: "mc",
          q: "Complete: 'If you receive unexpected money, _____.'",
          options: [
            "spend it all immediately",
            "save at least 50% of it",
            "forget about it"
          ],
          a: "save at least 50% of it",
          successtip: "✓ WINDFALL!\nSaving windfalls = accelerates wealth building! 💎"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no tienes fondo de emergencia, establece uno ahora'",
          words: ["If", "you", "don't", "have", "an", "emergency", "fund", "establish", "one", "now"],
          a: "If you don't have an emergency fund establish one now",
          extra: ["will", "establishing"],
          tip: "Establish = establecer/crear!",
          successtip: "✓ EMERGENCY FUND!\nEmergency funds = financial safety net! Essential! 🆘"
        }
      ]
    },

    // SET 10
    {
      id: "m9-u10-s10",
      title: "Set 10: Relationships & Communication 💬",
      context: `**Relationship Counselor**

"Healthy communication: If you're upset, express it calmly. If you disagree, listen first. If you make a mistake, apologize sincerely."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is mature relationship advice?",
          options: [
            "If you're angry, yell loudly",
            "If you're upset, express it calmly",
            "If you're mad, ignore them"
          ],
          a: "If you're upset, express it calmly",
          tip: "Express calmly = comunicar con calma!",
          successtip: "✓ EMOTIONAL INTELLIGENCE!\nCalm expression prevents escalation! Mature communication! 🗣️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no estás de acuerdo, escucha primero'",
          words: ["If", "you", "disagree", "listen", "first"],
          a: "If you disagree listen first",
          extra: ["will", "disagreeing"],
          successtip: "✓ ACTIVE LISTENING!\nListening before responding = sign of respect! 👂"
        },
        {
          type: "input",
          q: "Complete: 'If you make a mistake, _____ sincerely.' (apologize)",
          a: "apologize",
          tip: "Apologize = disculparse!",
          successtip: "✓ ACCOUNTABILITY!\nSincere apologies repair relationships! Character strength! 🙏"
        },
        {
          type: "match",
          q: "Match the relationship situation with advice:",
          matches: [
            { left: "If your partner seems distant,", right: "ask what's wrong" },
            { left: "If you feel misunderstood,", right: "clarify your feelings" },
            { left: "If there's a conflict,", right: "address it directly" }
          ],
          successtip: "✓ HEALTHY RELATIONSHIPS!\nDirect, honest communication builds strong bonds! 💑"
        },
        {
          type: "mc",
          q: "What does 'address it directly' mean?",
          options: [
            "Write down the address",
            "Talk about the problem openly",
            "Avoid the issue"
          ],
          a: "Talk about the problem openly",
          tip: "Address = abordar (a problem)!",
          successtip: "✓ DIRECT COMMUNICATION!\nAddressing issues directly prevents resentment! 🎯"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si alguien te critica, mantén la mente abierta'",
          words: ["If", "someone", "criticizes", "you", "keep", "an", "open", "mind"],
          a: "If someone criticizes you keep an open mind",
          extra: ["will", "criticized"],
          successtip: "✓ GROWTH MINDSET!\nOpen mind to criticism = opportunity to improve! 🌱"
        },
        {
          type: "input",
          q: "Complete: 'If you hurt someone's feelings, _____ responsibility.' (take)",
          a: "take",
          tip: "Take responsibility = asumir responsabilidad!",
          successtip: "✓ MATURITY!\nTaking responsibility = sign of emotional maturity! 🎖️"
        },
        {
          type: "match",
          q: "Match the communication advice:",
          matches: [
            { left: "If you want to be heard,", right: "speak clearly and calmly" },
            { left: "If you don't understand,", right: "ask for clarification" },
            { left: "If emotions run high,", right: "take a break" }
          ],
          successtip: "✓ CONFLICT RESOLUTION!\nThese skills prevent arguments from escalating! 🕊️"
        },
        {
          type: "mc",
          q: "Complete: 'If you value the relationship, _____.'",
          options: [
            "take it for granted",
            "invest time and effort",
            "ignore problems"
          ],
          a: "invest time and effort",
          tip: "Invest = invertir (time, effort)!",
          successtip: "✓ RELATIONSHIP INVESTMENT!\nAll relationships need ongoing effort! Worth it! 💝"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si necesitas espacio, comunícalo claramente'",
          words: ["If", "you", "need", "space", "communicate", "it", "clearly"],
          a: "If you need space communicate it clearly",
          extra: ["will", "communicating"],
          tip: "Space = espacio personal/tiempo a solas!",
          successtip: "✓ BOUNDARIES!\nCommunicating needs clearly = healthy boundaries! 🚧"
        }
      ]
    },

    // SET 11
    {
      id: "m9-u10-s11",
      title: "Set 11: Environmental & Social Responsibility 🌍",
      context: `**Sustainability Guide**

"Make a difference: If you care about the planet, reduce waste. If you see litter, pick it up. If you can walk instead of drive, do it!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which is environmental advice?",
          options: [
            "If you're done with it, throw it anywhere",
            "If you can walk instead of drive, do it",
            "If you use plastic, use more"
          ],
          a: "If you can walk instead of drive, do it",
          successtip: "✓ ECO-FRIENDLY!\nWalking reduces carbon footprint! Simple environmental action! 🚶"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si te importa el planeta, reduce desperdicios'",
          words: ["If", "you", "care", "about", "the", "planet", "reduce", "waste"],
          a: "If you care about the planet reduce waste",
          extra: ["will", "caring"],
          successtip: "✓ WASTE REDUCTION!\nReduce waste = #1 environmental action individuals can take! ♻️"
        },
        {
          type: "input",
          q: "Complete: 'If you see litter, _____ it up.' (pick)",
          a: "pick",
          tip: "Pick up = recoger!",
          successtip: "✓ COMMUNITY ACTION!\nPicking up litter = simple act of environmental citizenship! 🗑️"
        },
        {
          type: "match",
          q: "Match the eco-action:",
          matches: [
            { left: "If you're shopping,", right: "bring reusable bags" },
            { left: "If you're done with electronics,", right: "recycle them properly" },
            { left: "If you're buying coffee,", right: "use a reusable cup" }
          ],
          successtip: "✓ SUSTAINABILITY!\nSmall daily choices make BIG environmental impact! 🌱"
        },
        {
          type: "mc",
          q: "What does 'carbon footprint' refer to?",
          options: [
            "Footprints in coal",
            "Amount of CO2 emissions you cause",
            "Type of shoe"
          ],
          a: "Amount of CO2 emissions you cause",
          tip: "Carbon footprint = huella de carbono!",
          successtip: "✓ ENVIRONMENTAL AWARENESS!\nCarbon footprint = key climate change concept! B2 vocabulary! 🦶"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si puedes repararlo, no lo tires'",
          words: ["If", "you", "can", "repair", "it", "don't", "throw", "it", "away"],
          a: "If you can repair it don't throw it away",
          extra: ["will", "repairing"],
          successtip: "✓ REPAIR CULTURE!\nRepairing instead of replacing = reduces waste massively! 🔧"
        },
        {
          type: "input",
          q: "Complete: 'If you have influence, _____ it for positive change.' (use)",
          a: "use",
          tip: "Influence = influencia!",
          successtip: "✓ SOCIAL RESPONSIBILITY!\nUsing influence for good = leadership! 🌟"
        },
        {
          type: "match",
          q: "Match the social action:",
          matches: [
            { left: "If you witness injustice,", right: "speak up" },
            { left: "If you can volunteer,", right: "give your time" },
            { left: "If you have privilege,", right: "use it to help others" }
          ],
          successtip: "✓ CIVIC DUTY!\nThese actions create better communities! Be the change! ✊"
        },
        {
          type: "mc",
          q: "Complete: 'If you consume less, _____.'",
          options: [
            "you'll be unhappy",
            "you'll reduce environmental impact",
            "you'll need more"
          ],
          a: "you'll reduce environmental impact",
          tip: "Consume less = consumir menos!",
          successtip: "✓ MINIMALISM!\nConsuming less = better for planet AND your wallet! 🌍"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si ves discriminación, no seas cómplice'",
          words: ["If", "you", "see", "discrimination", "don't", "be", "complicit"],
          a: "If you see discrimination don't be complicit",
          extra: ["will", "saw"],
          tip: "Complicit = cómplice!",
          successtip: "✓ STAND UP!\nNot being complicit = active ally! Social justice! ✊"
        }
      ]
    },

    // SET 12
    {
      id: "m9-u10-s12",
      title: "Set 12: Mastery Challenge - All Contexts 🏆",
      context: `**Final Mastery Challenge**

You've mastered first conditional + imperative! Now apply it across ALL contexts: personal, professional, emergency, and social!`,
      
      questions: [
        {
          type: "mc",
          q: "Which shows the MOST sophisticated use?",
          options: [
            "If you're tired, sleep",
            "If you're experiencing burnout, reassess your work-life balance and implement boundaries",
            "If you're sleepy, rest"
          ],
          a: "If you're experiencing burnout, reassess your work-life balance and implement boundaries",
          tip: "Advanced vocabulary + complex solution!",
          successtip: "✓ B2 LEVEL!\nComplex problem + sophisticated vocabulary = advanced mastery! 🎓"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si enfrentas adversidad, aprovéchala como oportunidad de crecimiento'",
          words: ["If", "you", "face", "adversity", "use", "it", "as", "an", "opportunity", "for", "growth"],
          a: "If you face adversity use it as an opportunity for growth",
          extra: ["will", "faced"],
          tip: "Adversity = adversidad!",
          successtip: "✓ GROWTH MINDSET!\nReframing adversity = advanced emotional intelligence! 📈"
        },
        {
          type: "input",
          q: "Complete professionally: 'If stakeholders raise concerns, _____ them thoughtfully and incorporate feedback.' (address)",
          a: "address",
          tip: "Stakeholders = partes interesadas!",
          successtip: "✓ STAKEHOLDER MANAGEMENT!\nAddressing concerns thoughtfully = professional excellence! 💼"
        },
        {
          type: "match",
          q: "Match the advanced context:",
          matches: [
            { left: "Academic:", right: "If your hypothesis is disproven, revise your methodology" },
            { left: "Medical:", right: "If symptoms worsen despite treatment, consult a specialist" },
            { left: "Legal:", right: "If you face litigation, retain qualified counsel immediately" }
          ],
          successtip: "✓ PROFESSIONAL FIELDS!\nFirst conditional + imperative works in ALL professional contexts! 🎯"
        },
        {
          type: "mc",
          q: "Which combines MULTIPLE imperatives best?",
          options: [
            "If you're lost, ask someone",
            "If you're navigating unfamiliar territory, consult maps, ask locals, and stay aware of your surroundings",
            "If you don't know where you are, look around"
          ],
          a: "If you're navigating unfamiliar territory, consult maps, ask locals, and stay aware of your surroundings",
          tip: "Multiple actions = comprehensive advice!",
          successtip: "✓ COMPREHENSIVE!\nMultiple imperatives = thorough, detailed guidance! 🗺️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si aspiras a la excelencia, cultiva la disciplina, busca retroalimentación y nunca dejes de aprender'",
          words: ["If", "you", "aspire", "to", "excellence", "cultivate", "discipline", "seek", "feedback", "and", "never", "stop", "learning"],
          a: "If you aspire to excellence cultivate discipline seek feedback and never stop learning",
          extra: ["will", "aspired"],
          successtip: "✓ EXCELLENCE!\nThree powerful imperatives for success! Inspirational advice! 🌟"
        },
        {
          type: "input",
          q: "Complete with advanced vocabulary: 'If you encounter ethical dilemmas, _____ your core values and act with integrity.' (consult)",
          a: "consult",
          tip: "Ethical dilemmas = dilemas éticos!",
          successtip: "✓ ETHICS!\nConsulting values = moral decision-making! Character advice! 🧭"
        },
        {
          type: "match",
          q: "Match the life domain with advice:",
          matches: [
            { left: "Physical health:", right: "If you're sedentary, incorporate movement into your daily routine" },
            { left: "Mental health:", right: "If you're struggling, don't hesitate to seek professional help" },
            { left: "Financial health:", right: "If you're debt-free, maintain that freedom through disciplined spending" }
          ],
          successtip: "✓ HOLISTIC WELLNESS!\nFirst conditional works for ALL aspects of life! 🌈"
        },
        {
          type: "mc",
          q: "Which shows NATIVE-level fluency?",
          options: [
            "If you're sad, be happy",
            "If you're going through hell, keep going",
            "If you're unhappy, smile"
          ],
          a: "If you're going through hell, keep going",
          tip: "Famous Winston Churchill quote!",
          successtip: "✓ IDIOMATIC!\n'Going through hell' = facing extreme difficulty. Native expression! 🔥"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si quieres dejar un legado, vive con propósito, sirve a otros y marca una diferencia'",
          words: ["If", "you", "want", "to", "leave", "a", "legacy", "live", "with", "purpose", "serve", "others", "and", "make", "a", "difference"],
          a: "If you want to leave a legacy live with purpose serve others and make a difference",
          extra: ["will", "leaving"],
          successtip: "✓ 🎉 MASTERY ACHIEVED! 🎉\nYou've conquered first conditional + imperative! You can now give advice, solve problems, and inspire others in English across ALL contexts—from health to business, from emergencies to life philosophy! This is essential B2 communication! 🏆🌟"
        }
      ]
    }
  ]
};
